import { useParams, Link } from "react-router-dom";
import { products } from "@/content/products";
import usePageMeta from "@/hooks/usePageMeta";
import { PageHero } from "@/components/sections/PageHero";
import { ProductSection } from "@/components/sections/ProductSection";
import { CtaBand } from "@/components/sections/CtaBand";
import { SectionHeader } from "@/components/common/SectionHeader";
import { ExtLink } from "@/components/common/ExtLink";
import { Reveal } from "@/components/motion/Reveal";
import { footer } from "@/content/site";
import NotFound from "./NotFound";

const Facts = ({ facts }) => (
  <section className="pb-16" data-testid="product-facts">
    <div className="container-x">
      <dl className={`grid border-y border-line sm:grid-cols-2 ${facts.length === 4 ? "lg:grid-cols-4" : "lg:grid-cols-5"}`}>
        {facts.map((f, i) => (
          <Reveal key={f.label} delay={i * 0.05} className={`py-6 sm:px-5 ${i ? "border-t border-line sm:border-t-0 lg:border-l" : "sm:pl-0"} ${i % 2 ? "sm:border-l" : ""} ${i > 1 ? "sm:border-t lg:border-t-0" : ""}`}>
            <dt className="font-mono text-[12px] uppercase tracking-[0.16em] text-accent">{f.label}</dt>
            <dd className="mt-2 text-[15px] leading-snug text-ink">{f.value}</dd>
          </Reveal>
        ))}
      </dl>
    </div>
  </section>
);

const Evidence = ({ evidence }) => (
  <section className="section-y border-t border-line" data-testid="product-evidence">
    <div className="container-x">
      <SectionHeader eyebrow={evidence.eyebrow ?? "Clinical evidence"} title={evidence.title} body={evidence.note} className="mb-12" />
      {evidence.statement && (
        <Reveal className="flex flex-col gap-4 border-y border-line py-7 sm:flex-row sm:items-center sm:justify-between" data-testid="product-evidence-statement">
          <p className="text-base text-ink sm:text-lg">{evidence.statement.text}</p>
          <Link to={evidence.statement.to} className="shrink-0 text-[15px] font-medium text-accent underline-offset-4 hover:underline" data-testid="product-evidence-link">
            {evidence.statement.label}
          </Link>
        </Reveal>
      )}
      {evidence.studies && (
      <ol className="border-t border-line">
        {evidence.studies.map((s, i) => (
          <Reveal as="li" key={s.title} delay={i * 0.04} className="grid gap-3 border-b border-line py-7 md:grid-cols-12 md:gap-8">
            <span className="font-mono text-[13px] text-accent md:col-span-1">{String(i + 1).padStart(2, "0")}</span>
            <div className="md:col-span-6">
              <h3 className="font-display text-xl font-semibold leading-snug tracking-[-0.02em] text-ink sm:text-2xl">{s.title}</h3>
              <p className="mt-2 text-[13px] text-muted">{s.source}</p>
            </div>
            <p className="text-[15px] leading-relaxed text-ink/90 md:col-span-5">{s.finding}</p>
          </Reveal>
        ))}
      </ol>
      )}
    </div>
  </section>
);

const Availability = ({ p }) => (
  <section className="section-y border-t border-line" data-testid="product-availability">
    <div className="container-x grid gap-10 lg:grid-cols-12">
      <Reveal className="lg:col-span-5">
        <p className="eyebrow mb-5">Partners</p>
        <h2 className="h-section">Availability and licensing</h2>
      </Reveal>
      <Reveal delay={0.1} className="space-y-5 lg:col-span-7 lg:pt-10">
        {p.availability.map((t) => (
          <p key={t} className="text-base leading-relaxed text-ink/90 sm:text-lg">{t}</p>
        ))}
        <div className="flex flex-wrap gap-x-6 gap-y-3 pt-4 text-[15px]">
          {p.links.map((l, i) => (
            <ExtLink key={l.href} href={l.href} testId={`product-link-${i}`}>
              {l.label}
            </ExtLink>
          ))}
        </div>
        <p className="pt-6 text-[13px] leading-relaxed text-muted">{footer.disclaimer} Approved uses and availability vary by market.</p>
      </Reveal>
    </div>
  </section>
);

export default function Product() {
  const { slug } = useParams();
  const p = products[slug];
  usePageMeta(p ? p.meta : { title: "Page not found | SPC", description: "" });
  if (!p) return <NotFound />;
  return (
    <div data-testid={`product-page-${p.slug}`}>
      <PageHero hero={p.hero} testId="product-hero" />
      <Facts facts={p.facts} />
      {p.sections.map((s) => (
        <ProductSection key={s.id} s={s} />
      ))}
      {p.evidence && <Evidence evidence={p.evidence} />}
      <Availability p={p} />
      <CtaBand title={p.cta} />
    </div>
  );
}
